import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Bell, Mail } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { fetchProduct, fetchProducts, subscribeNewsletter } from "../lib/api";
import {
  getProductName,
  getProductDescription,
  getProductHighlights,
} from "../i18n";
import { ProductCard } from "../components/ProductCard";
import { ProductImageZoom } from "../components/ProductImageZoom";
import { Lightbox } from "../components/Lightbox";
import { ProductImg } from "../components/ProductImg";
import { Noindex } from "../components/Noindex";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [related, setRelated] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Newsletter (Be first to shop)
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    setLoading(true);
    setActiveImage(0);
    fetchProduct(slug)
      .then((p) => setProduct(p))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!product) return;
    fetchProducts({ category: product.category })
      .then((list) =>
        setRelated(list.filter((p) => p.id !== product.id).slice(0, 4)),
      )
      .catch(() => setRelated([]));
  }, [product]);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    try {
      await subscribeNewsletter(email.trim(), lang);
      setSubscribed(true);
      setEmail("");
      toast.success(t.newsletter.success);
    } catch {
      toast.error(t.newsletter.error);
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <main className="bg-black min-h-screen pt-32">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="aspect-square bg-zinc-900" />
            <div className="space-y-4">
              <div className="h-6 w-1/3 bg-zinc-900" />
              <div className="h-12 w-2/3 bg-zinc-900" />
              <div className="h-4 w-full bg-zinc-900" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="bg-black min-h-screen pt-32 pb-24 flex items-center justify-center">
        <Noindex title={t.seo.notFound} />
        <div className="text-center px-6">
          <h1 className="h-display text-white text-3xl md:text-4xl">
            {t.notFound.title}
          </h1>
          <Link
            to="/shop"
            data-testid="product-not-found-back"
            className="mt-8 inline-block text-yellow-400 underline"
          >
            ← {t.checkout.backToShop}
          </Link>
        </div>
      </main>
    );
  }

  const highlights = getProductHighlights(product, lang);

  return (
    <main data-testid="product-page" className="bg-black min-h-screen pt-24 md:pt-28">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 pt-8 pb-24">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-yellow-400 transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4" /> {t.checkout.backToShop}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
          {/* Editorial image column */}
          <div className="lg:col-span-7">
            <ProductImageZoom
              src={product.images[activeImage]}
              alt={getProductName(product, lang)}
              onOpenLightbox={() => setLightboxOpen(true)}
            />

            {product.images.length > 1 && (
              <div className="mt-4 grid grid-cols-4 gap-3">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    data-testid={`thumb-${idx}`}
                    aria-label={`Image ${idx + 1}`}
                    className={`relative aspect-square overflow-hidden rounded-sm transition-all duration-300 ${
                      activeImage === idx
                        ? "ring-2 ring-yellow-400 ring-offset-2 ring-offset-black"
                        : "ring-1 ring-white/10 hover:ring-white/40 opacity-75 hover:opacity-100"
                    }`}
                    style={{
                      background:
                        "radial-gradient(120% 120% at 50% 20%, #FAFAF7 0%, #EFEEEA 60%, #E5E4DF 100%)",
                    }}
                  >
                    <div className="absolute inset-2 flex items-center justify-center">
                      <ProductImg
                        src={img}
                        className="max-w-full max-h-full object-contain"
                        small
                      />
                    </div>
                  </button>
                ))}
              </div>
            )}

            <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-white/40">
              {t.product.zoomHint}
            </p>
          </div>

          {/* Info column */}
          <div className="lg:col-span-5 flex flex-col">
            <div className="flex items-center gap-2 mb-5">
              {product.badge && (
                <span className="bg-yellow-400 text-black text-[10px] font-black tracking-[0.25em] uppercase px-2.5 py-1">
                  {product.badge === "THE ORIGINAL" ? t.hero.originalBadge : product.badge}
                </span>
              )}
              <span
                data-testid="coming-soon-badge"
                className="text-[10px] uppercase tracking-[0.25em] font-bold text-yellow-400 border border-yellow-400/40 px-2.5 py-1"
              >
                {t.comingSoon.badge}
              </span>
            </div>

            <p className="overline mb-3">{product.category}</p>
            <h1 className="h-display text-white text-4xl sm:text-5xl md:text-6xl leading-[0.95]">
              {getProductName(product, lang)}
            </h1>
            <div className="mt-5 flex items-center gap-4">
              <p className="text-2xl font-bold">${product.price.toFixed(0)} USD</p>
              <span className="text-xs uppercase tracking-[0.2em] text-white/40">
                {t.product.sku}: {product.sku}
              </span>
            </div>

            <div className="mt-8 space-y-4 text-white/70 leading-relaxed">
              {getProductDescription(product, lang)
                .split(/\n\n+/)
                .map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
            </div>

            {/* Highlights */}
            {highlights.length > 0 && (
              <div className="mt-8">
                <p className="overline mb-4">{t.product.highlights}</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-white/80">
                  {highlights.map((h, i) => (
                    <li
                      key={i}
                      data-testid={`highlight-${i}`}
                      className="flex items-start gap-2"
                    >
                      <span className="w-1.5 h-1.5 bg-yellow-400 mt-2 flex-shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Size + Color info (display-only while Coming Soon) */}
            <div className="mt-8 grid grid-cols-2 gap-6">
              {product.sizes?.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-white/50 mb-2">
                    {t.product.size}
                  </p>
                  <p className="text-sm font-bold uppercase tracking-wide">
                    {product.sizes.join(" · ")}
                  </p>
                </div>
              )}
              {product.colors?.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-white/50 mb-2">
                    {t.product.color}
                  </p>
                  <p className="text-sm font-bold uppercase tracking-wide">
                    {product.colors.join(" · ")}
                  </p>
                </div>
              )}
            </div>

            {/* Coming Soon panel */}
            <div
              data-testid="coming-soon-panel"
              className="mt-10 border border-yellow-400/30 bg-gradient-to-br from-yellow-400/[0.06] to-transparent p-6 md:p-7"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 bg-yellow-400 animate-pulse" />
                <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-yellow-400">
                  {t.comingSoon.title}
                </p>
              </div>
              <p className="text-sm text-white/70 leading-relaxed">
                {t.comingSoon.body}
              </p>

              <button
                type="button"
                disabled
                data-testid="coming-soon-button"
                className="mt-6 w-full bg-white/10 text-white/90 h-14 font-bold uppercase tracking-[0.3em] text-xs border border-white/15 cursor-not-allowed flex items-center justify-center gap-3"
                aria-disabled="true"
              >
                <Bell className="w-4 h-4" />
                {t.comingSoon.button}
              </button>

              {/* Inline newsletter — Be first to shop */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-white mb-1">
                  {t.comingSoon.newsletterCta}
                </p>
                <p className="text-xs text-white/50 mb-4">
                  {t.comingSoon.newsletterHint}
                </p>
                {subscribed ? (
                  <div
                    data-testid="product-notify-success"
                    className="flex items-center gap-2 text-yellow-400 text-sm border border-yellow-400/40 bg-yellow-400/10 px-4 py-3"
                  >
                    <Mail className="w-4 h-4" />
                    {t.newsletter.success}
                  </div>
                ) : (
                  <form
                    onSubmit={handleNotify}
                    className="flex flex-col sm:flex-row gap-0"
                  >
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.newsletter.placeholder}
                      data-testid="product-notify-input"
                      className="flex-1 bg-transparent border border-white/20 focus:border-yellow-400 outline-none px-4 py-3 text-sm text-white placeholder-white/40 transition-colors"
                    />
                    <button
                      type="submit"
                      disabled={subscribing}
                      data-testid="product-notify-submit"
                      className="bg-yellow-400 text-black px-6 py-3 font-bold uppercase tracking-[0.25em] text-xs hover:bg-yellow-300 transition-colors disabled:opacity-40"
                    >
                      {subscribing ? "..." : t.newsletter.cta}
                    </button>
                  </form>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-24">
            <h2 className="h-display text-white text-3xl md:text-4xl mb-8">
              {t.product.youMightLike}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-10">
              {related.map((r) => (
                <ProductCard key={r.id} product={r} />
              ))}
            </div>
          </div>
        )}
      </div>

      <Lightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={product.images}
        initialIndex={activeImage}
        alt={getProductName(product, lang)}
      />
    </main>
  );
}
